import React, { useEffect, useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { PLATFORM_COLORS, PLATFORM_ICONS } from '../../../config/icons';
import { useTranslate } from '@tolgee/react';
import { 
  ALL, 
  ENGAGEMENT, 
  ENGAGEMENT_RATE, 
  IMPRESSIONS, 
  PLATFORM_LIST_TOLGEE_NAMES, 
  PLATFORM_METRICS_AVAILABILITY, 
  REACH 
} from '../../../config/constants';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday','sunday'];
const HOURS = Array.from({ length: 24 }, (_, i) => {
  const hour = (23-i) < 10 ? `0${23-i}` : (23-i).toString();
  return `${hour}:00`;
});
const SOURCES = [IMPRESSIONS, REACH, ENGAGEMENT];

type Props = {
  source: string;
  chartData: any[];
  platform: string;
  onSourceChange?: (newSource: string) => void;
  color: string; 
};

const HeatmapChart: React.FC<Props> = ({ source, chartData, platform, onSourceChange, color }) => {
  const { t } = useTranslate();
  const locationDetails = useSelector((state: any) => state.locationDetails);

  const translatedDays = useMemo(() => DAYS.map(day => t(`best_time_to_post.${day}`)), [t]);

  const transformedData = useMemo(() => {
    return chartData.map(([hour, day, value]) => [day, 23 - hour, value]);
  }, [chartData]);

  const maxValue = useMemo(() => {
    const calculatedMax = transformedData.map(item => item[2] || 0).reduce((max, val) => Math.max(max, val), 0);
    // If all values are 0, return a small positive value to ensure visualMap works correctly
    return calculatedMax > 0 ? calculatedMax : 0.001;
  }, [transformedData]);

  const topThreeCoords = useMemo(() => {
    // Create a copy of the data with non-null and non-zero values and sort by value (descending)
    const validData = transformedData
      .filter(item => item[2] != null && item[2] > 0)
      .sort((a, b) => (b[2] || 0) - (a[2] || 0));

    // Take the top 3 items (or fewer if there aren't 3 valid items)
    const topThree = validData.slice(0, 3);

    // Return the coordinates of each item
    return topThree.map(item => [item[0], item[1]]);
  }, [transformedData]);

  const chartOptions = useMemo(() => ({
    tooltip: { show: false },
    grid: {
      top: 10,
      left: 10,
      right: 0,
      bottom: 40,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: translatedDays,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#eee' } },
      axisLabel: {
        color: '#555',
        fontSize: 14,
        interval: 0,
        rotate: 0,
        margin: 8,
        fontFamily: 'Inter, sans-serif',
      },
      splitArea: {
        show: true,
        areaStyle: { color: ['#fff', '#fff'] }
      }
    },
    yAxis: {
      type: 'category',
      data: HOURS,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#eee' } },
      axisLabel: {
        color: '#555',
        fontSize: 14,
        margin: 8,
        fontFamily: 'Inter, sans-serif'
      },
      splitArea: {
        show: true,
        areaStyle: { color: ['#fff', '#fff'] }
      }
    },
    visualMap: {
      min: 0,
      max: maxValue,
      show: false,
      inRange: {
        color: ['#fefefe', color]
      }
    },
    series: [
      {
        type: 'heatmap',
        data: transformedData,
        itemStyle: {
          borderColor: '#ccc',
          borderWidth: 1
        },
        emphasis: {
          itemStyle: {
            borderColor: '#999',
            borderWidth: 1
          }
        },
        label: {
          show: true,
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1,
            color:"white"
          },
          formatter: (params: any) => {
            // Check if current cell is one of the top three
            const isTopThree = topThreeCoords.some(coords => 
              params.value[0] === coords[0] && params.value[1] === coords[1]
            );

            if (isTopThree) {
              return t(`bestTimeToPost.top_three_high`).toString();
            }
            return '';
          },
          color: 'white',
          fontSize: 12,
          fontWeight: 'bold',
          fontFamily: 'Inter, sans-serif'
        }
      }
    ]
  }), [transformedData, translatedDays, maxValue, topThreeCoords, t]);

  // Check if the metric is available for this platform
  const isMetricAvailable = (platform, source) => {
    // If it's the ALL platform or if there's no specific configuration for this platform, assume all metrics are available
    if (platform === ALL || !PLATFORM_METRICS_AVAILABILITY[platform]) {
      return true;
    }

    // Check if the metric is available for this platform
    return PLATFORM_METRICS_AVAILABILITY[platform][source] !== false;
  };

  return (
    <>
      {(platform === ALL || locationDetails?.platforms[platform]) ?
        <div className="card mb-4 p-3">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            <div>
              <i
                className={PLATFORM_ICONS[platform]}
                style={{ color: PLATFORM_COLORS[platform], fontSize: 20 }}
              />{' '}
              <span className="fw-bold ms-1 fs-3" >
            {t(PLATFORM_LIST_TOLGEE_NAMES[platform])}
                {' '}
                <OverlayTrigger
                  placement="auto"
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <Tooltip >
                      <div className={"text-start"} style={{whiteSpace: "break-spaces"}}>
                        {t('bestTimeToPost.tooltip_info')}
                      </div>
                    </Tooltip>
                  }
                >
              <i className="fa-regular fa-circle-info text-muted fs-4"></i>
            </OverlayTrigger>
          </span>
            </div>
            <select
              className="form-select w-auto"
              value={source}
              onChange={(e) => onSourceChange?.(e.target.value)}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {SOURCES.map(src => (
                <option key={src} value={src}>
                  {t(src)}
                </option>
              ))}
            </select>
          </div>

          {/* Chart or Info Message */}
          {!isMetricAvailable(platform, source) ? (
            <div
              style={{
                height: 500,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              <i className="fa-duotone fa-chart-scatter text-muted mb-3" style={{ fontSize: '3rem' }}></i>
              <p className="text-muted fs-4">
                {t('best_time_to_post.metric_not_available')}
              </p>
              <p className="text-muted" style={{ fontSize: '1.05rem' }}>
                {t('best_time_to_post.does_not_support')}
              </p>
            </div>
          ) : chartData.length > 0 ? (
            <ReactECharts option={chartOptions} style={{ height: 500 }} />
          ) : (
            <div
              style={{
                height: 500,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              <i className="fa-duotone fa-chart-scatter text-muted mb-3" style={{ fontSize: '3rem' }}></i>
              <p className="text-muted fs-4">
                {t('best_time_to_post.no_data_available')}
              </p>
              <p className="text-muted" style={{ fontSize: '1.05rem' }}>
                {t('platform')}: {platform} - {t(source)}
              </p>
            </div>
          )}

          {/* Legend - Only show if the metric is available */}
          {isMetricAvailable(platform, source) && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
              <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                {t(`bestTimeToPost.least_${source}_legend`)}
              </span>
              <div style={{ width: 80, height: 8, background: `linear-gradient(to right, #fefefe, ${color})` }} />
              <span className="text-muted" style={{ fontSize: '0.9rem' }}>{t(`bestTimeToPost.most_${source}_legend`)}</span>
            </div>
          )}
        </div>
        : ""}
    </>
  );
};

export default HeatmapChart;
